/**
 * Reveal-screen ambient background. Static R-honeycomb watermark over
 * a dark fill — matches Figma 702:8017 / 702:5485 (calm, no animated
 * layers). Earlier revisions ran aurora orbs + dust motes + pulse
 * glow + vignette on a canvas; those were removed to align with the
 * Figma direction.
 *
 * The rarity tint resolver `glowColorFor` lives in its own module
 * (`./glowColorFor`) now, so this file exports only the component.
 */

import { useIsSleek } from '../theme';

export function RevealBackground() {
  // Sleek keeps the page flat (partner Loom 2026-10-06: no background
  // textures) — the gradient fill stays, the watermark goes.
  const isSleek = useIsSleek();
  return (
    <div
      data-page="reveal"
      data-co-component="RevealPageBackground"
      className="absolute inset-0 overflow-hidden"
      // No `pointer-events-none` here on purpose — Design Constructor's
      // right-click inspector targets the element returned by the
      // browser's hit-test, which skips pointer-events:none nodes and
      // would otherwise fall through to the iframe body. Content above
      // (tiles, buttons, nav arrows) sits on `z-10` so it still owns its
      // own clicks; only empty page background catches them.
      //
      // Page-scoped `--ui-bg-gradient` is set on this element via the
      // `[data-page='reveal']` rule in index.css — partner-level tokens
      // (`--co-RevealPageBackground-*` → `--co-PageBackground-*` → palette
      // → Figma default) cascade through that.
      style={{ backgroundImage: 'var(--ui-bg-gradient)' }}
      aria-hidden
    >
      {/* Hex‑R watermark — inline SVG `<pattern>` instead of a tiled PNG.
          Reasons:
            1. crisp at every DPR (no raster blur on retina/zoom);
            2. no double‑line seams at tile edges — each hex side is a
               single geometric stroke shared across neighbouring tiles;
            3. the R is rendered as `<text>`, so weight + colour follow
               CSS / theme instead of being baked into a bitmap. */}
      {!isSleek && (
      <svg
        className="pointer-events-none absolute -inset-[5%] h-[110%] w-[110%]"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
        style={{
          // Watermark fades toward the bottom so the lower CTA area
          // reads on plain dark, matching the Figma reference.
          maskImage:
            'linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 38%, rgba(0,0,0,0) 92%)',
          WebkitMaskImage:
            'linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 38%, rgba(0,0,0,0) 92%)',
        }}
      >
        <defs>
          {/* Pointy‑top hexagon grid. side s = 18 — half the previous
              size so the watermark mesh / R glyphs are denser but still
              quiet, per the Figma reference.
                hex w = √3·s ≈ 31.18, hex h = 2s = 36, row period 1.5s = 27.
              Tile 62.35×54 holds two full hexes with R on the offset
              row and three split hexes (outline only) on the even row. */}
          <pattern
            id="reveal-hex-r"
            patternUnits="userSpaceOnUse"
            width="62.35"
            height="54"
          >
            {/* Offset row centres y=13.5 — two full hexes with R */}
            <polygon
              points="15.59,-4.5 31.18,4.5 31.18,22.5 15.59,31.5 0,22.5 0,4.5"
              fill="none"
              stroke="rgba(255,255,255,0.018)"
              strokeWidth="1"
            />
            <polygon
              points="46.76,-4.5 62.35,4.5 62.35,22.5 46.76,31.5 31.18,22.5 31.18,4.5"
              fill="none"
              stroke="rgba(255,255,255,0.018)"
              strokeWidth="1"
            />
            <text
              x="15.59"
              y="18"
              textAnchor="middle"
              fontFamily="Inter, sans-serif"
              fontWeight={800}
              fontSize="12"
              fill="rgba(255,255,255,0.035)"
            >
              R
            </text>
            <text
              x="46.76"
              y="18"
              textAnchor="middle"
              fontFamily="Inter, sans-serif"
              fontWeight={800}
              fontSize="12"
              fill="rgba(255,255,255,0.035)"
            >
              R
            </text>

            {/* Even row centres y=40.5 — three half hexes split across
                the left edge, middle and right edge of the tile. No
                letters here so glyphs never get cut by the boundary. */}
            <polygon
              points="0,22.5 15.59,31.5 15.59,49.5 0,58.5 -15.59,49.5 -15.59,31.5"
              fill="none"
              stroke="rgba(255,255,255,0.018)"
              strokeWidth="1"
            />
            <polygon
              points="31.18,22.5 46.76,31.5 46.76,49.5 31.18,58.5 15.59,49.5 15.59,31.5"
              fill="none"
              stroke="rgba(255,255,255,0.018)"
              strokeWidth="1"
            />
            <polygon
              points="62.35,22.5 77.94,31.5 77.94,49.5 62.35,58.5 46.76,49.5 46.76,31.5"
              fill="none"
              stroke="rgba(255,255,255,0.018)"
              strokeWidth="1"
            />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#reveal-hex-r)" />
      </svg>
      )}
    </div>
  );
}
